import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MessagingService } from './services/messaging.service';
import { NotificationSoundService } from './services/notification-sound.service';
import { UiBusyOverlayComponent } from './ui/busy-overlay/ui-busy-overlay.component';
import { UiToastHostComponent } from './ui/toast/ui-toast.component';

/**
 * The root shell: the routed view plus the two global feedback surfaces.
 *
 * What left this component in the D0 foundation wave, and where it went:
 *
 *  - `updateLanguageSettings()` -> `LocaleService`. It wrote `<html dir>`, added
 *    a `lang-ar`/`lang-en` class and persisted `localStorage.lang`, and it was
 *    ONE of four places doing that (nav, the auth components and shared-table
 *    were the others), which is why switching language left parts of the UI in
 *    the previous direction until a reload. LocaleService now writes
 *    `<html lang dir>` and is provided as the CDK `Directionality`.
 *  - `translate.setDefaultLang/use` -> `LocaleService.init()`, called from an
 *    APP_INITIALIZER so it is awaited before the first paint (BIZ-02).
 *  - `<ngx-spinner>` -> `<ui-busy-overlay>`, driven by the ref-counted
 *    `LoaderService` the loading interceptor already feeds.
 *  - the dead `isLoading` placeholder (an `of(false)` subscribed to in
 *    `ngOnInit`, feeding a `<mat-progress-spinner>` no template rendered) is
 *    gone with the commented-out LoaderService it was standing in for.
 *
 * UNCHANGED: the FCM permission request, `receiveMessage()` and the sound
 * unlock still run here, in the same order, on the same lifecycle hook.
 */
@Component({
  standalone: true,
  imports: [RouterOutlet, UiToastHostComponent, UiBusyOverlayComponent],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly messagingService = inject(MessagingService);
  private readonly notificationSound = inject(NotificationSoundService);

  ngOnInit(): void {
    this.notificationSound.armUnlockOnUserGesture();
    this.messagingService.requestPermission();
    this.messagingService.receiveMessage();
  }
}
